/**
 * 셰이더 선컴파일 — 「처음 보는 것은 보이기 **전에** 번역해 둔다」 (2026-08-16)
 *
 * ── 왜 필요한가 ────────────────────────────────────────────────────────────
 * three 는 어떤 (재질 × 지오메트리 속성 × 광원 × 그림자 × 안개) 조합을 **처음 그릴 때**
 * 그 자리에서 GPU 프로그램을 컴파일한다. ANGLE D3D11(윈도우 크롬)은 GLSL→HLSL→D3D 를
 * 타서 개당 100~300ms 다. 그리고 그게 **프레임 안에서** 일어나므로 그대로 멈춤이다.
 *
 *   실측(2026-08-16 · 녹화 d8x3kj): 새 지역에 걸어 들어가는 한 프레임에 **34개**가
 *   한꺼번에 컴파일돼 **3,620ms** 멈췄다(그중 gl.render 안이 3,361ms).
 *   같은 녹화에서 만든 프로그램 102개 중 버린 건 9개뿐 — **낭비가 아니라 처음 배우는 것**이다.
 *   그래서 이건 캐시로 못 막는다(캐시는 이미 걸었다: 0b040bda·85d22587). 남은 길은
 *   **먼저 배워 두는 것** 하나다.
 *
 * ── 이 수리가 서는 두 개의 사실 ────────────────────────────────────────────
 * ① three 의 `compile()` 은 **재질을 `scene.traverse` 로 모은다**(가시성을 안 본다).
 *    광원만 `targetScene.traverseVisible` 이다. 그래서 `compileAsync(새것, camera, scene)`
 *    처럼 부르면 **새것이 화면에서 빠져 있어도** 컴파일이 되고 광원은 진짜 씬 것을 쓴다.
 *    이 비대칭이 없으면 「숨긴 채로 미리 컴파일」이 성립하지 않는다.
 * ② `compileAsync` 는 `KHR_parallel_shader_compile` 이 있으면 `program.isReady()` 로
 *    폴링한다 — 즉 **드라이버가 백그라운드 스레드에서** 컴파일하고 우리는 기다리기만 한다.
 *    확장이 없으면 이득이 줄지만 손해는 아니다(어차피 낼 값을 먼저 내는 것뿐).
 *
 * ── ★ 숨길 때 `visible` 을 쓰지 않는다 ─────────────────────────────────────
 * 이 게임엔 거리 컬링이 여럿 있고(farCull·LANDMARK_CULL·blobDist) **걔들이 매 프레임
 * `visible` 을 쓴다.** 우리가 같은 손잡이를 잡으면 복원할 때 컬러가 끄려던 것을 켜 버린다 —
 * 「멀리 있는 게 안 사라진다」가 되고, 원인이 여기라는 걸 아무도 못 찾는다.
 * 그래서 **`layers` 로 뺀다.** 렌더러는 `object.layers.test(camera.layers)` 로 그릴지를
 * 정하므로 레이어를 잠깐 비우면 안 그려지고, `visible` 은 컬러 것으로 그대로 둔다.
 *
 * ── 첫 판은 데우지 않는다 ──────────────────────────────────────────────────
 * 처음 씬이 서는 순간엔 **모든 게 새것**이라 전부 숨기면 화면이 통째로 비었다가 뜬다.
 * 그 비용은 어차피 로딩 중에 내는 것이라 이득도 없다. 그래서 첫 스캔은 **표시만 하고
 * 손을 안 댄다**(seed) — 그 뒤에 들어오는 것만 진짜 대상이다.
 */
import * as THREE from 'three';
import { holdResources } from './resourceLifetime.js';
import { compileAsyncSafe, compileForTarget } from './compileForTarget.js';
import { stickyBool } from './debugFlags.js';

const warmed = new WeakSet();     // 이미 데웠거나 데울 필요가 없다고 판정한 것

// ── 축출과의 경쟁 조건 핀 ──────────────────────────────────────
// `gl.compileAsync()` 는 GPU 드라이버가 끝낼 때까지 진행 중(inflight)인 비동기 작업이다.
// 그 사이 이 메시가 속한 GLB 가 `glbCacheProbe.sweepGlbCache()` 에 의해 축출·dispose 되면
// three 내부 `checkMaterialsReady()` 가 `materialProperties.currentProgram.isReady()` 를
// 읽다가 `program` 이 undefined 라 던진다 — `Uncaught TypeError: reading 'isReady'`
// (2026-08-19 인계 §2.7, 08-20 실사용자 로그에도 동일 파일:줄로 재현, 원인 미상이었음).
// 두 모듈이 서로를 몰라서 나는 경쟁이므로, 워밍 중인 지오메트리 uuid 를 핀으로 공개해
// glbCacheProbe 가 "지금 보이든 안 보이든 축출 후보에서 뺀다"로 참조하게 한다.
const pinnedGeoUUIDs = new Set();
const pinnedGeoCounts = new Map();
export const getPinnedGeoUUIDs = () => pinnedGeoUUIDs;
const SCAN_EVERY = 6;             // 몇 프레임마다 새것을 찾나 (매 프레임 훑을 이유가 없다)
const BATCH = 12;                 // 한 번에 데울 최대 개수 — 너무 크면 그 묶음이 통째로 늦게 뜬다
const HOLD_MS = 1500;             // 이 시간 넘게 안 끝나면 그냥 보여준다(영영 숨는 것보다 낫다)

const S = {
  frame: 0,
  inflight: 0,
  seeded: false,
  warmedN: 0,      // 실제로 데운 개수 (계측)
  heldN: 0,        // 시한 초과로 그냥 보여준 개수
  off: typeof location !== 'undefined'
    && new URLSearchParams(location.search).get('warm') === '0',
};

if (typeof window !== 'undefined') window.__shaderWarm = S;

/**
 * 진단용 — **지금 살아 있는 프로그램의 이름표**. (2026-08-17)
 *
 * `renderer.info.programs` 는 개수만 준다. 「74개가 늘었다」까지는 알아도 **무엇이** 늘었는지를
 * 모르면 고칠 자리를 못 고른다 — 실제로 그래서 「새 메시를 못 따라간다」로 틀린 가설을 세웠다
 * (실측 한 판: 메시는 4개 느는데 프로그램은 74개 늘었다).
 * three 의 `WebGLProgram` 은 `name`(재질 이름)과 `cacheKey`(정의·플래그 전부)를 들고 있다.
 * cacheKey 는 수천 자라 그대로 내면 못 읽으므로 **짧은 해시**로 접어 종류별로 센다.
 * 소비처: `front/scripts/probe_shader_warm.mjs`.
 */
const _hash = (s) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
};
S.programKeys = () => {
  const list = S._gl?.info?.programs;
  if (!list) return null;
  return Array.from(list, (p) => `${p.name || '?'}#${_hash(String(p.cacheKey || ''))}`);
};

/**
 * 진단 2 — **cacheKey 원문**. 「무엇이 늘었나」 다음의 질문은 「**왜** 갈렸나」다. (2026-08-17)
 *
 * `programKeys` 로 세어 보니 재질 **하나**가 프로그램을 8~10개씩 갖고 있었다
 * (`hanji` 10 · `wall`·`plaster`·`clay`·`stone`·`roof` 8 · 익명 32). 정상은 1~2개다
 * (본체 + 그림자 깊이). 즉 새 물건이 온 게 아니라 **있던 재질이 다른 플래그로 다시 컴파일**된
 * 것이고, 이건 개수를 줄여야 없어진다 — 스케줄링(선컴파일)으로는 못 푼다는 게 이미 실증됐다.
 *
 * three 의 `getProgramCacheKey` 는 값을 **고정 순서로 배열에 밀어 넣고 `join(',')`** 한다
 * (`three.module.js` 의 `getProgramCacheKeyParameters`). 그래서 같은 `shaderID` 를 가진 키
 * 둘은 **토큰 위치가 정확히 맞고**, 다른 토큰의 **자리 번호가 곧 필드 이름**이다.
 * 해시로 접으면 그 정보가 사라지므로 여기서는 원문을 그대로 낸다(소비처가 자리로 이름을 찾는다).
 *
 * ⚠ 키 하나가 수백 자다. 화면에 뿌리지 말고 프로브(`probe_program_split.mjs`)가 받아 접는다.
 */
S.programDump = () => {
  const list = S._gl?.info?.programs;
  if (!list) return null;
  return Array.from(list, (p) => ({ name: p.name || '?', key: String(p.cacheKey || '') }));
};

/**
 * 진단 3 — **씬의 광원 수**. 위 키에서 갈리는 자리가 `numPointLights` 류로 나오면, 그다음
 * 질문은 「그 수가 언제 몇으로 변하나」다. three 는 `visible` 인 광원만 센다(=`intensity 0`
 * 은 개수에서 안 빠진다). `BuildLights` 가 2026-08-11 에 이미 슬롯을 고정했지만 그 주석 스스로
 * **「0 아니면 14 두 상태」**라고 적고 있고, 그 전환 한 번이 곧 전 재질 재컴파일이다.
 */
S.lightCount = () => {
  const sc = S._scene;
  if (!sc) return null;
  const c = { dir: 0, point: 0, spot: 0, hemi: 0, rect: 0, shadow: 0 };
  sc.traverseVisible((o) => {
    if (!o.isLight) return;
    if (o.isDirectionalLight) c.dir += 1;
    else if (o.isPointLight) c.point += 1;
    else if (o.isSpotLight) c.spot += 1;
    else if (o.isHemisphereLight) c.hemi += 1;
    else if (o.isRectAreaLight) c.rect += 1;
    if (o.castShadow) c.shadow += 1;
  });
  return c;
};

/** 그릴 수 있는 것인가 — three 의 compile() 이 재질을 모으는 기준과 같게 맞춘다. */
const isDrawable = (o) => o.isMesh || o.isPoints || o.isLine || o.isSprite;
const DRAW_FLAGS = ['isMesh', 'isPoints', 'isLine', 'isSprite'];
const isHeightfield = (o) => typeof o.name === 'string' && o.name.startsWith('heightfield');
const materialsOf = (o) => (Array.isArray(o.material) ? o.material : [o.material]);

/** WebGLShadowMap.getDepthMaterial 과 같은 프로그램 키가 나오게 만든 그림자 depth 재질. 키마다 하나를 재사용한다. */
const SHADOW_SIDE = { [THREE.FrontSide]: THREE.BackSide, [THREE.BackSide]: THREE.FrontSide, [THREE.DoubleSide]: THREE.DoubleSide };
const depthMats = new Map();
let depthRT = null;
const depthFor = (gl, m) => {
  const side = gl.shadowMap.type === THREE.VSMShadowMap ? (m.shadowSide ?? m.side) : (m.shadowSide ?? SHADOW_SIDE[m.side]);
  const alphaTest = m.alphaToCoverage === true ? 0.5 : m.alphaTest;
  const key = `${side}|${+!!m.map}${+!!m.alphaMap}${+(alphaTest > 0)}${+!!m.displacementMap}`;
  let d = depthMats.get(key);
  if (!d) { d = new THREE.MeshDepthMaterial(); d.side = side; depthMats.set(key, d); }
  d.map = m.map; d.alphaMap = m.alphaMap; d.alphaTest = alphaTest;
  d.displacementMap = m.displacementMap; d.displacementScale = m.displacementScale; d.displacementBias = m.displacementBias;
  return d;
};

/**
 * 그림자를 드리우는 것들의 depth 변형을 같은 비동기 컴파일에 태운다. 안 하면 다시 보이는 첫 프레임에
 * 그림자 패스가 그 프로그램을 동기로 링크하며, 앞서 줄 선 비동기 컴파일이 다 끝날 때까지 메인 스레드가 선다.
 * 재질은 compile() 의 동기 부분 동안만 바꿨다가 되돌린다. 그림자가 꺼져 있으면 null.
 */
function compileShadowDepth(gl, bag, camera, scene, compiling) {
  if (!gl.shadowMap?.enabled) return null;
  const swaps = [];
  const skipped = [];
  for (const o of compiling) {
    const dm = o.isMesh && o.castShadow
      && (o.customDepthMaterial || (Array.isArray(o.material) ? o.material.map((m) => m && depthFor(gl, m)) : o.material && depthFor(gl, o.material)));
    if (dm) { swaps.push([o, o.material]); o.material = dm; continue; }
    // 그림자 안 드리우는 것은 이 컴파일에서 뺀다 — 안 빼면 안개 뗀 본 재질 변형이 쓸모없이 구워진다.
    // compile() 은 isMesh·isPoints·isLine·isSprite 로 거르므로 그 표식만 잠깐 끈다(material 대입은 troika Text 가 깨진다).
    const flags = DRAW_FLAGS.map((f) => o[f]);
    skipped.push([o, flags]);
    for (const f of DRAW_FLAGS) o[f] = false;
  }
  S.depthWarmN = (S.depthWarmN || 0) + swaps.length;
  S._depthMats = depthMats;
  const restore = () => {
    for (const [o, m] of swaps) o.material = m;
    for (const [o, flags] of skipped) DRAW_FLAGS.forEach((f, i) => { o[f] = flags[i]; });
  };
  if (!swaps.length) { restore(); return null; }
  depthRT ||= new THREE.WebGLRenderTarget(1, 1);
  // 그림자 패스는 씬 없이 그려 fog 비트가 꺼진 키를 쓴다 — 같게 맞추려 동기 부분 동안만 fog 를 뗀다.
  const fog = scene.fog;
  scene.fog = null;
  try { return compileForTarget(gl, bag, camera, scene, depthRT); } finally { scene.fog = fog; restore(); }
}

/**
 * 숨긴 채로 컴파일하고 끝나면(또는 HOLD_MS 시한) 되돌린다. 리소스·지오메트리 핀은 그 사이 유지.
 * @param {Array<import('three').Object3D>} found 이미 `layers.mask = 0` 인 그리기 객체
 * @param {Array<[import('three').Object3D, number]>} held [객체, 숨기기 전 mask]
 */
function warmHidden(gl, scene, camera, target, found, held, onCompiled = null) {
  const compiling = new Set();
  for (const root of found) root.traverse((o) => { if (isDrawable(o)) compiling.add(o); });
  const geometries = [...compiling].map((o) => o.geometry).filter(Boolean);
  const releaseResources = holdResources([...geometries,
    ...[...compiling].flatMap((o) => Array.isArray(o.material) ? o.material : [o.material])]);
  for (const g of geometries) {
    pinnedGeoUUIDs.add(g.uuid);
    pinnedGeoCounts.set(g.uuid, (pinnedGeoCounts.get(g.uuid) || 0) + 1);
  }
  const unpin = () => {
    releaseResources();
    for (const g of geometries) {
      const n = pinnedGeoCounts.get(g.uuid) - 1;
      if (n > 0) pinnedGeoCounts.set(g.uuid, n);
      else { pinnedGeoCounts.delete(g.uuid); pinnedGeoUUIDs.delete(g.uuid); }
    }
  };

  const restore = () => {
    for (const [o, mask] of held) {
      // ⚠ 우리가 뺀 그 값 그대로 되돌린다. 그 사이 남이 레이어를 바꿨으면 그쪽을 존중한다.
      if (o.layers.mask === 0) o.layers.mask = mask;
    }
    S.inflight -= 1;
    S.warmedN += held.length;
  };

  S.inflight += 1;
  let done = false;
  const once = (bumpHeld) => { if (done) return; done = true; if (bumpHeld) S.heldN += held.length; restore(); };

  // 시한 — 컴파일이 영영 안 끝나는 기기가 있어도 화면에서 사라진 채로 두지 않는다.
  const timer = setTimeout(() => once(true), HOLD_MS);

  try {
    // 첫 인자 = **새것만 담은 임시 그룹**. 이게 재질 수집 대상이고,
    // 세 번째 인자(targetScene)가 광원·그림자를 주는 진짜 씬이다.
    const bag = new THREE.Group();
    // ⚠ 실제로 add 하지 않는다 — 부모를 바꾸면 월드 행렬이 틀어진다. children 만 빌린다.
    bag.children = found;
    const compile = typeof location !== 'undefined' && new URLSearchParams(location.search).get('warmtarget') === '0'
      ? compileAsyncSafe(gl, bag, camera, scene) : compileForTarget(gl, bag, camera, scene, target);
    const depth = SHADOW_DEPTH_WARM ? compileShadowDepth(gl, bag, camera, scene, compiling) : null;
    (depth ? Promise.all([compile, depth]) : compile)
      .then(() => { clearTimeout(timer); if (onCompiled) onCompiled(compiling); once(false); })
      .catch(() => { clearTimeout(timer); once(false); })
      .finally(unpin);
  } catch (_) {
    unpin();
    clearTimeout(timer);
    once(false);
  }
  return held.length;
}

/**
 * 붙는 순간 숨김(`?warmeager=0` 이면 꺼짐) — 씬에 붙은 새 그리기 객체를 add 안에서 바로
 * `layers.mask = 0` 으로 빼고 큐에 넣는다. 다음 pump(같은 프레임이면 렌더 전)가 데운다.
 * 6프레임 훑기 사이에 붙은 것이 한 번 먼저 그려져 렌더 안에서 컴파일되는 경주를 막는다.
 * 건너뛰는 것: seed 전 · 다른 씬 · 지형 타일 · 이미 데운 것 · 같은 재질 지문이 이미 컴파일된 것
 * (마지막은 숨기지 않고 warmed 로만 찍는다 — 이미 보이는 것을 숨기면 깜빡임이다).
 */
const EAGER = stickyBool('warmeager', true);
const SHADOW_DEPTH_WARM = stickyBool('warmdepth', true);
const pending = [];               // [객체, 숨기기 전 mask] — 다음 pump 가 데운다
let pendingTimer = 0;
S.eager = EAGER;
S.eagerN = 0;
S.eagerSkipN = 0;

// 프로그램을 가를 만한 재질·지오 속성의 근사 지문. 같은 지문이 한 번 컴파일됐으면 새 재질 사본도
// 캐시 적중으로 보고 숨기지 않는다(몬스터·청크는 개체마다 재질을 복제하므로 currentProgram 만으론 매번 「새것」).
const seenSig = new Set();
const sigOf = (o, m) => {
  const g = o.geometry;
  return `${m.type}|${m.name}|${m.customProgramCacheKey ? m.customProgramCacheKey() : ''}`
    + `|${+!!m.map}${+!!m.normalMap}${+!!m.emissiveMap}${+!!m.roughnessMap}${+!!m.metalnessMap}${+!!m.alphaMap}${+!!m.envMap}${+!!m.aoMap}`
    + `|${m.vertexColors}|${m.transparent}|${m.alphaTest > 0}|${m.side}|${m.flatShading}|${m.fog}`
    + `|${+!!o.isSkinnedMesh}${+!!o.isInstancedMesh}${+!!o.isBatchedMesh}`
    + `|${g ? `${Object.keys(g.attributes).join(',')}:${Object.keys(g.morphAttributes).length}` : ''}`
    + `|${m.defines ? JSON.stringify(m.defines) : ''}`;
};
const learnSigs = (gl, objs) => {
  for (const o of objs) for (const m of materialsOf(o)) {
    if (m && gl.properties.has(m) && gl.properties.get(m).currentProgram) seenSig.add(sigOf(o, m));
  }
};
const isKnown = (gl, o) => materialsOf(o).every((m) => {
  if (!m) return true;
  if (gl.properties.has(m) && gl.properties.get(m).currentProgram) { seenSig.add(sigOf(o, m)); return true; }
  return seenSig.has(sigOf(o, m));
});

function restorePending(bumpHeld) {
  if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = 0; }
  const batch = pending.splice(0);
  for (const [o, mask] of batch) if (o.layers.mask === 0) o.layers.mask = mask;
  if (bumpHeld) S.heldN += batch.length;
}

function onAttach(root) {
  const gl = S._gl;
  if (S.off || !S.seeded || !gl || !S._scene) return;
  let top = root;
  while (top.parent) top = top.parent;
  if (top !== S._scene) return;
  root.traverse((o) => {
    if (!isDrawable(o) || warmed.has(o)) return;
    warmed.add(o);
    if (!o.material || isHeightfield(o)) return;
    if (isKnown(gl, o)) { S.eagerSkipN += 1; return; }
    pending.push([o, o.layers.mask]);
    o.layers.mask = 0;
    S.eagerN += 1;
  });
  // pump 가 멈춰도(창 숨김·컴포넌트 해제) 영영 숨은 채로 두지 않는다.
  if (pending.length && !pendingTimer) pendingTimer = setTimeout(() => { pendingTimer = 0; restorePending(true); }, HOLD_MS);
}

if (EAGER && !THREE.Object3D.prototype.add.__warmEager) {
  const origAdd = THREE.Object3D.prototype.add;
  const add = function add(object) {
    const r = origAdd.apply(this, arguments);
    if (arguments.length === 1 && object && object.parent === this && object.isObject3D) onAttach(object);
    return r;
  };
  add.__warmEager = true;
  THREE.Object3D.prototype.add = add;
}

/**
 * 새로 들어온 것들을 찾아 **레이어에서 빼고** 컴파일이 끝나면 되돌린다.
 * @returns {number} 이번에 데우기 시작한 개수
 */
function warmBatch(gl, scene, camera, target) {
  // ── 첫 판 — **씬 전체**를 표시만 하고 손대지 않는다 ──────────────────────
  // ⚠ 여기서 BATCH 로 자르면 안 된다.
  //   자르면 seed 가 12개만 찍고 끝나고, **이미 화면에 떠 있던 나머지 수백 개가**
  //   그 뒤 스캔에서 「새것」으로 잡혀 12개씩 순서대로 숨겨진다. 데울 이유가 없는 것을
  //   숨기는 것이고, 그게 곧 「멀쩡히 보이던 게 깜빡인다」다.
  //   seed 는 목록을 만드는 일이지 일을 하는 일이 아니라서 상한이 필요 없다.
  if (!S.seeded) {
    S.seeded = true;
    scene.traverse((o) => { if (isDrawable(o)) warmed.add(o); });
    return 0;
  }

  const found = [];
  scene.traverse((o) => {
    if (found.length >= BATCH) return;
    if (!isDrawable(o) || warmed.has(o)) return;
    // 재질이 없으면 컴파일할 것도 없다. 배열 재질도 그대로 넘긴다(three 가 알아서 편다).
    if (!o.material) { warmed.add(o); return; }
    // ⚠ 지형 타일은 **데우지 않는다.** 한 장이 화면의 큰 몫을 덮어서 한 프레임만 빠져도
    //   「바닥이 사라졌다」로 보이고, 지형은 이미 워커 두 개로 자기 비동기 파이프라인을
    //   갖고 있다(hfWorker·hfPaintWorker). 위험은 큰데 벌 이득이 작다.
    if (isHeightfield(o)) { warmed.add(o); return; }
    found.push(o);
  });
  if (!found.length) return 0;

  const held = [];
  for (const o of found) {
    warmed.add(o);
    held.push([o, o.layers.mask]);
    o.layers.mask = 0;            // 어느 카메라와도 안 겹친다 = 안 그려진다
  }
  return warmHidden(gl, scene, camera, target, found, held);
}

/**
 * 매 프레임 호출한다. 스캔은 SCAN_EVERY 프레임마다, 컴파일은 한 번에 한 묶음만.
 * @param {import('three').WebGLRenderer} gl
 */
export function pumpShaderWarm(gl, scene, camera, target = null) {
  // 끄는 문 둘 — **배포 없이** 끌 수 있어야 한다. 이 수리는 「잠깐 안 그리기」로 이득을 사는
  // 것이라, 실기에서 깜빡임이 보이면 그 자리에서 꺼서 원인을 가를 수 있어야 한다.
  //   `?warm=0` 으로 새로고침  ·  콘솔에서 `__shaderWarm.off = true`
  if (!gl || !scene || !camera) return;
  S._gl = gl;                              // 진단용 참조 — `off` 보다 **먼저** 잡는다
  S._scene = scene;                        // (꺼 놓고 대조군을 재는 실행에서도 이름표는 봐야 한다)
  S._camera = camera;
  S._target = target;
  if (S.off) { if (pending.length) restorePending(false); return; }
  S.frame += 1;
  if (pending.length) {
    if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = 0; }
    const held = pending.splice(0);
    warmHidden(gl, scene, camera, target, held.map(([o]) => o), held, (objs) => learnSigs(gl, objs));
  }
  if (S.inflight > 0) return;              // 한 묶음이 끝나야 다음 묶음
  if (S.frame % SCAN_EVERY !== 0) return;
  warmBatch(gl, scene, camera, target);
}

/**
 * 씬 밖 객체(`root`)의 재질을 실제 화면과 같은 렌더 타깃·광원·안개 키로 미리 컴파일한다.
 * 아직 pump 가 한 번도 안 돌았으면 null(호출자가 다음 기회에 다시 부른다). 끝나면 root 로 resolve.
 */
export function prewarmDetached(root) {
  if (!S._gl || !S._scene || !S._camera || !root) return null;
  return compileForTarget(S._gl, root, S._camera, S._scene, S._target || null);
}

export default pumpShaderWarm;
